import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../store/axios';
import Modal from '../../../components/Modal';

// Live Streaming — Cybernate's live video service (live.cybernate.ai), managed
// through the gateway's /streaming API for the signed-in workspace.

const errMsg = (e) => {
  // A gateway without the /streaming routes answers 404 for every call
  if (e?.response?.status === 404 && e.config?.url?.startsWith('/streaming')) {
    return 'Live Streaming isn’t available on the API yet. Try again shortly, or contact support if this persists.';
  }
  return e?.response?.data?.message || e?.message || 'Something went wrong';
};

function ConnectCard() {
  const snippet = `npm install cybernate-ai

const { CybernateAI } = require('cybernate-ai');
const client = new CybernateAI(process.env.CYBERNATE_API_KEY);

const stream = await client.streaming.createStream({ title: 'My live show' });
// stream.ingest.rtmp_url + stream.ingest.stream_key → broadcaster (shown once)
// stream.playback.hls_url → viewers`;
  return (
    <div className="dash-card">
      <div className="dash-card-head">
        <div>
          <div className="dash-card-title">Connect your app</div>
          <div className="dash-card-desc">
            Use any API key with <strong>Full access</strong> — the same key you use for AI.{' '}
            <Link to="/dashboard/keys" style={{ color: 'var(--accent)' }}>Manage API keys →</Link>
          </div>
        </div>
      </div>
      <pre style={{ margin: 0, padding: '14px 20px', fontFamily: 'var(--font-mono)', fontSize: 12, lineHeight: 1.6, color: 'var(--fg-mid)', overflowX: 'auto', whiteSpace: 'pre' }}>{snippet}</pre>
      <div className="dash-card-foot" style={{ fontSize: 12, color: 'var(--fg-dim)' }}>
        REST: <code>POST https://api.cybernate.ai/v1/streaming/streams</code> with header <code>X-Api-Key</code>. Read-only keys can list streams and usage but can’t create or end streams.
      </div>
    </div>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function formatDuration(seconds) {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h ? `${h}h ${m}m` : m ? `${m}m ${s}s` : `${s}s`;
}
const minutes = (s) => (s ? Math.round(s / 60).toLocaleString() : '0');

const STATUS_BADGE = {
  live: { cls: 'badge-live', label: '● live' },
  disconnected: { cls: 'badge-pending', label: 'reconnecting' },
  idle: { cls: 'badge-test', label: 'ready' },
  ended: { cls: 'badge-test', label: 'ended' },
};

function CopyRow({ label, value, secret }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="field" style={{ marginBottom: 12 }}>
      <label>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg)', border: `1px solid ${secret ? 'rgba(107,255,184,0.25)' : 'var(--line)'}`, borderRadius: 'var(--r-sm)', padding: '9px 12px' }}>
        <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg)', flex: 1, wordBreak: 'break-all' }}>{value}</code>
        <button type="button" className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11, flexShrink: 0 }} onClick={copy}>
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function CreateStreamModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [externalId, setExternalId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.post('/streaming/streams', {
        title: title.trim(),
        ...(externalId.trim() && { external_id: externalId.trim() }),
      });
      setCreated(data.data);
      onCreated();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth={560}>
      <div className="modal-head" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div className="modal-title">{created ? 'Stream ready' : 'New live stream'}</div>
          <p className="modal-sub">
            {created ? 'Point your encoder or app at these details. The stream key is shown once.' : 'Create a stream, then broadcast to it from OBS, a mobile app or the Cybernate SDK.'}
          </p>
        </div>
        <button type="button" className="modal-close" onClick={onClose}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </button>
      </div>

      {created ? (
        <>
          <div className="modal-body">
            <CopyRow label="RTMP server" value={created.ingest.rtmp_url} />
            <CopyRow label="Stream key (secret — shown once)" value={created.ingest.stream_key} secret />
            <CopyRow label="Full publish URL (mobile / SDK)" value={created.ingest.stream_url} secret />
            <CopyRow label="Playback URL (HLS — share with viewers)" value={created.playback.hls_url} />
            <p style={{ fontSize: 11, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)', letterSpacing: '0.04em', marginTop: 4 }}>
              Unused streams expire after 24 hours. Create a new stream for each broadcast.
            </p>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </>
      ) : (
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="field">
              <label>Title</label>
              <input type="text" placeholder="e.g. Friday flash sale" value={title} maxLength={200}
                onChange={(e) => setTitle(e.target.value)} autoFocus />
            </div>
            <div className="field">
              <label>Your reference (optional)</label>
              <input type="text" placeholder="e.g. your own stream or user id" value={externalId} maxLength={200}
                onChange={(e) => setExternalId(e.target.value)} />
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="btn-loader" /> : 'Create stream →'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function WebhookCard() {
  const [settings, setSettings] = useState(null);
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/streaming/settings')
      .then(({ data }) => { setSettings(data.data); setUrl(data.data.webhook_url || ''); })
      .catch((e) => setError(errMsg(e)));
  }, []);

  const run = async (name, fn) => {
    setBusy(name); setError(null); setNotice(null);
    try { await fn(); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  };

  const save = () => run('save', async () => {
    const { data } = await api.patch('/streaming/settings', { webhook_url: url.trim() || null });
    setSettings(data.data);
    setNotice('Webhook URL saved');
  });
  const rotate = () => run('rotate', async () => {
    const { data } = await api.post('/streaming/settings/webhook-secret/rotate');
    setSecret(data.data.webhook_secret);
    setSettings(data.data);
    setNotice('New signing secret — update your server, the old one no longer verifies');
  });
  const reveal = () => run('reveal', async () => {
    const { data } = await api.get('/streaming/settings/webhook-secret');
    setSecret(data.data.webhook_secret);
  });
  const test = () => run('test', async () => {
    await api.post('/streaming/settings/webhook-test');
    setNotice('Test event queued — check your endpoint and the events list below');
  });

  return (
    <div className="dash-card">
      <div className="dash-card-head">
        <div>
          <div className="dash-card-title">Webhooks</div>
          <div className="dash-card-desc">stream.live · stream.disconnected · stream.reconnected · stream.ended — signed with Cybernate-Signature</div>
        </div>
      </div>
      <div className="dash-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div className="alert alert-error">{error}</div>}
        {notice && <div className="alert alert-success">{notice}</div>}
        <div className="field">
          <label>Endpoint URL</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="url" placeholder="https://your-api.com/webhooks/streaming" value={url} onChange={(e) => setUrl(e.target.value)} style={{ flex: 1 }} />
            <button className="btn btn-primary" onClick={save} disabled={busy === 'save' || !settings}>{busy === 'save' ? <span className="btn-loader" /> : 'Save'}</button>
          </div>
        </div>
        <div className="field">
          <label>Signing secret</label>
          {secret
            ? <CopyRow label="" value={secret} secret />
            : <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-mid)' }}>{settings?.webhook_secret || '—'}</code>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {!secret && <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={reveal} disabled={!!busy}>Reveal</button>}
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={rotate} disabled={!!busy}>Rotate</button>
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={test} disabled={!!busy || !settings?.webhook_url}>Send test event</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventsCard({ refreshKey }) {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get('/streaming/events', { params: { limit: 15 } })
      .then(({ data }) => setEvents(data.data || []))
      .catch((e) => setError(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const redeliver = async (id) => {
    try { await api.post(`/streaming/events/${id}/redeliver`); setTimeout(load, 1500); } catch (e) { setError(errMsg(e)); }
  };

  const deliveryColor = { delivered: 'var(--signal)', failed: '#ff6b6b', pending: 'var(--fg-mid)', skipped: 'var(--fg-faint)' };

  return (
    <div className="dash-card">
      <div className="dash-card-head">
        <div>
          <div className="dash-card-title">Recent events</div>
          <div className="dash-card-desc">Kept 30 days · redeliver anything your endpoint missed</div>
        </div>
        <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={load}>Refresh</button>
      </div>
      {error && <div className="alert alert-error" style={{ margin: 16 }}>{error}</div>}
      {!events.length ? (
        <div className="dash-empty" style={{ padding: '28px 24px' }}>
          <div className="dash-empty-title" style={{ fontSize: 13 }}>No events yet</div>
          <p className="dash-empty-sub">Events appear when a stream goes live, drops or ends.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead><tr><th>Event</th><th>Stream</th><th>Time</th><th>Webhook</th><th></th></tr></thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="td-mono">{e.type}</td>
                  <td className="td-name">{e.data?.stream?.title || (e.stream_id ? e.stream_id.slice(-6) : '—')}</td>
                  <td className="td-mono" style={{ color: 'var(--fg-dim)' }}>{formatDate(e.created_at)}</td>
                  <td className="td-mono" style={{ color: deliveryColor[e.delivery?.status] || 'var(--fg-dim)' }}>
                    {e.delivery?.status || '—'}{e.delivery?.last_error ? ` · ${e.delivery.last_error}` : ''}
                  </td>
                  <td className="td-action">
                    {e.delivery?.status !== 'skipped' && (
                      <button onClick={() => redeliver(e.id)} style={{ fontSize: 12, color: 'var(--fg-dim)', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>Redeliver</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Streaming() {
  const [streams, setStreams] = useState([]);
  const [usage, setUsage] = useState(null);
  const [viewers, setViewers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirm, setConfirm] = useState(null); // { id, action }
  const [refreshKey, setRefreshKey] = useState(0);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        api.get('/streaming/streams', { params: { limit: 50 } }),
        api.get('/streaming/usage'),
      ]);
      setStreams(s.data.data || []);
      setUsage(u.data.data);
      setError(null);
      const live = (s.data.data || []).filter((x) => x.status === 'live' || x.status === 'disconnected');
      const counts = await Promise.all(live.map((x) => api.get(`/streaming/streams/${x.id}/viewers`).then((r) => [x.id, r.data.data]).catch(() => [x.id, null])));
      setViewers(Object.fromEntries(counts));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh every 10s while anything is live
  const anyLive = streams.some((s) => s.status === 'live' || s.status === 'disconnected');
  useEffect(() => {
    clearInterval(timer.current);
    if (anyLive) timer.current = setInterval(() => { load(); setRefreshKey((k) => k + 1); }, 10000);
    return () => clearInterval(timer.current);
  }, [anyLive, load]);

  const act = async (id, action) => {
    try {
      if (action === 'end') await api.post(`/streaming/streams/${id}/end`);
      if (action === 'delete') await api.delete(`/streaming/streams/${id}`);
      setConfirm(null);
      await load();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(errMsg(e));
      setConfirm(null);
    }
  };

  const liveNow = streams.filter((s) => s.status === 'live').length;
  const totals = usage?.totals;

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <div>
          <p className="dash-sub">PRODUCT</p>
          <h1 className="dash-page-title">Live Streaming</h1>
          <p className="dash-page-desc">Low-latency live video for your apps — RTMP/SRT in, HLS out, billed from your credits.</p>
        </div>
        <button className="btn btn-primary" style={{ flexShrink: 0, marginTop: 4 }} onClick={() => setShowCreate(true)}>
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          New stream
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="stat-grid stat-grid-3">
        <div className="stat-card">
          <span className="stat-label">Live now</span>
          <div className="stat-num">{loading ? '—' : liveNow}</div>
          <div className="stat-delta stat-delta-neutral">{streams.length} stream{streams.length !== 1 ? 's' : ''} total</div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Viewer minutes</span>
          <div className="stat-num">{totals ? totals.viewerMinutes.toLocaleString() : '—'}</div>
          <div className="stat-delta stat-delta-neutral">last 30 days · {totals ? totals.broadcastMinutes.toLocaleString() : '—'} live min</div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Streaming spend</span>
          <div className="stat-num">{totals ? `₦${totals.amountNgn.toLocaleString()}` : '—'}</div>
          <div className="stat-delta stat-delta-neutral">{totals ? `${totals.tokensCharged.toLocaleString()} credits · last 30 days` : 'from your credits'}</div>
        </div>
      </div>

      <div className="dash-card">
        <div className="dash-card-head">
          <div>
            <div className="dash-card-title">Streams</div>
            <div className="dash-card-desc">{anyLive ? 'Updating every 10s while live' : 'Newest first'}</div>
          </div>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={load}>Refresh</button>
        </div>
        {loading && !streams.length ? (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[1, 2, 3].map((i) => <div key={i} style={{ height: 44, background: 'var(--line)', borderRadius: 6, animation: 'shimmer 1.4s ease-in-out infinite' }} />)}
          </div>
        ) : !streams.length ? (
          <div className="dash-empty">
            <div className="dash-empty-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="11" height="10" rx="2" stroke="currentColor" strokeWidth="1.4"/><path d="M13 9l5-3v8l-5-3" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>
            </div>
            <div className="dash-empty-title">No streams yet</div>
            <p className="dash-empty-sub">Create a stream to get an RTMP URL and stream key, or use <code>client.streaming.createStream()</code> from the SDK.</p>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>New stream →</button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr><th>Stream</th><th>Status</th><th>Started</th><th>Duration</th><th>Viewers</th><th>Watched</th><th></th></tr>
              </thead>
              <tbody>
                {streams.map((s) => {
                  const badge = STATUS_BADGE[s.status] || STATUS_BADGE.idle;
                  const v = viewers[s.id];
                  const live = s.status === 'live' || s.status === 'disconnected';
                  return (
                    <tr key={s.id}>
                      <td className="td-name">
                        {s.title || 'Untitled stream'}
                        {s.external_id && <div className="td-mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>{s.external_id}</div>}
                      </td>
                      <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                      <td className="td-mono" style={{ color: 'var(--fg-dim)' }}>{formatDate(s.started_at)}</td>
                      <td className="td-mono">{live ? formatDuration(Math.floor((Date.now() - new Date(s.started_at)) / 1000)) : formatDuration(s.stats.duration_seconds)}</td>
                      <td className="td-mono">{live ? (v ? `${v.current} now · peak ${v.peak}` : '…') : s.stats.peak_viewers ? `peak ${s.stats.peak_viewers}` : '—'}</td>
                      <td className="td-mono">{minutes(live ? v?.viewer_seconds : s.stats.viewer_seconds)} min</td>
                      <td className="td-action">
                        {confirm?.id === s.id ? (
                          <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button onClick={() => act(s.id, confirm.action)} style={{ fontSize: 11, color: '#ff6b6b', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>
                              Confirm {confirm.action}
                            </button>
                            <button onClick={() => setConfirm(null)} style={{ fontSize: 11, color: 'var(--fg-dim)', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>Cancel</button>
                          </span>
                        ) : (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button onClick={() => navigator.clipboard?.writeText(s.playback.hls_url)} title={s.playback.hls_url} style={{ fontSize: 12, color: 'var(--fg-dim)', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>Copy URL</button>
                            {s.status !== 'ended' && (
                              <button onClick={() => setConfirm({ id: s.id, action: 'end' })} style={{ fontSize: 12, color: 'var(--fg-dim)', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>End</button>
                            )}
                            {!live && (
                              <button onClick={() => setConfirm({ id: s.id, action: 'delete' })} style={{ fontSize: 12, color: 'var(--fg-dim)', padding: '4px 8px', borderRadius: 'var(--r-sm)' }}>Delete</button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="dash-card-foot" style={{ fontSize: 12, color: 'var(--fg-dim)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span>
            {usage?.rates
              ? `Billed from credits: ${usage.rates.tokensPerBroadcastMinute} per live minute + ${usage.rates.tokensPerViewerMinute} per viewer minute`
              : 'Billed from your credits per live minute and viewer minute'}
          </span>
          <a href="/docs" style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>Streaming docs →</a>
        </div>
      </div>

      <ConnectCard />
      <WebhookCard />
      <EventsCard refreshKey={refreshKey} />

      {showCreate && <CreateStreamModal onClose={() => setShowCreate(false)} onCreated={() => { load(); setRefreshKey((k) => k + 1); }} />}
    </div>
  );
}
